import { skipToken } from '@apollo/client'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Link } from 'react-router'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { registryMap } from '@/routes/registry-credentials/utils/registry-map'
import type {
  DockerImageCredentialsQuery,
  DockerImageCredentialsQueryVariables,
} from '@/components/form/docker-image-input/registry-selector.types.gen'

const DOCKER_IMAGE_CREDENTIALS = gql`
  query DockerImageCredentials($idCompany: String!) {
    company(id: $idCompany) {
      id
      dockerRegistryCredentials {
        id
        name
        registry
        createdAt
      }
    }
  }
`

export const RegistrySelector = () => {
  const { idCompany } = useIdCompany()
  const { getCompanyPath } = useCompanyPath()
  const form = useFormContext()
  const { watch, resetField } = form

  const source = watch('source')

  const { data } = useSuspenseQuery<
    DockerImageCredentialsQuery,
    DockerImageCredentialsQueryVariables
  >(
    DOCKER_IMAGE_CREDENTIALS,
    source === 'dockerImage' ? { variables: { idCompany } } : skipToken
  )

  const credentials = data?.company?.dockerRegistryCredentials ?? []

  useEffect(() => {
    return () => {
      resetField('idDockerRegistryCredential')
    }
  }, [resetField])

  return (
    <FormField
      control={form.control}
      name="idDockerRegistryCredential"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Registry credential (optional)</FormLabel>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a docker credential" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {credentials.map((credential) => {
                const RegistryLogo = registryMap[credential.registry]?.logo
                return (
                  <SelectItem key={credential.id} value={credential.id}>
                    {RegistryLogo ? <RegistryLogo className="size-4" /> : null}
                    {credential.name}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          <FormDescription>
            You can set up a registry credential to pull your Docker image from
            a private registry. To add a new credential, visit{' '}
            <Link
              className="underline"
              to={getCompanyPath({ pathname: '/registry-credentials' })}
            >
              registry credentials
            </Link>
            .
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
